import * as moment from 'moment';
import { App } from 'ionic-angular';
import { BehaviorSubject, Observable } from 'rxjs';
import { CalendarEvent } from 'angular-calendar';
import { ClientServiceProvider } from '../../../clients/services/client-service/client-service';
import { DataSource } from '../../interfaces/data-source.interface';
import { DisplayServiceProvider } from '../../../core/services/display-service/display-service';
import { FilterInput } from '../../../../submodules/sellions_layout_renderer/projects/sellions-layout-renderer-lib/src/lib/interfaces/filter-input.interface';
import { FilterOutput } from '../../../../submodules/sellions_layout_renderer/projects/sellions-layout-renderer-lib/src/lib/interfaces/filter-outpt.interface';
import { FiltersConfig } from '../../../../submodules/sellions_layout_renderer/projects/sellions-layout-renderer-lib/src/lib/interfaces/filters-config.interface';
import { FilterServiceProvider, FilterType } from '../../../core/services/filter-service/FilterServiceProvider';
import { IClientModel } from '../../../clients/interfaces/i-client-model.interface';
import { Injectable } from '@angular/core';
import { PlayerServiceProvider } from '../../../core/services/player-service/player-service';
import { SellionsCalendarEvent } from '../../interfaces/sellions-calendar-event.interface';
import { SellionsEvent } from '../events-service/events-service';

@Injectable()
export class CalendarServiceProvider {
  private _selectedDate: BehaviorSubject<moment.Moment> = new BehaviorSubject<moment.Moment>(moment());
  public readonly selectedDate$: Observable<moment.Moment> = this._selectedDate.asObservable();

  private _currentEvents: BehaviorSubject<CalendarEvent[]> = new BehaviorSubject<CalendarEvent[]>([]);
  public readonly currentEvents$: Observable<CalendarEvent[]> = this._currentEvents.asObservable();

  private _loading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  public readonly loading$: Observable<boolean> = this._loading.asObservable();

  private dataSources: DataSource[];
  private colors = {
    primary: '#2D3E50',
    secondary: '#979797',
  };

  constructor(
    private app: App,
    private clientService: ClientServiceProvider,
    private displayService: DisplayServiceProvider,
    private filterService: FilterServiceProvider,
    private playerService: PlayerServiceProvider,
  ) {
    this.filterService.subscribe(FilterType.CALENDAR, () => this._loading.next(false));
  }

  public setDataSources(sources: DataSource[]) {
    this.dataSources = sources;
  }

  public async getEvents(): Promise<SellionsCalendarEvent[]> {
    if (!this.dataSources) return Promise.resolve([]);
    const promises = this.dataSources.map((source) => source.getEvents());
    const events = await Promise.all(promises);
    return this.flattenEvents(events);
  }

  public async filter(filters: FilterOutput[]): Promise<SellionsCalendarEvent[]> {
    if (!this.dataSources) return Promise.resolve([]);
    const promises = this.dataSources.map((source) => source.filter(filters));
    const events = await Promise.all(promises);
    return this.flattenEvents(events);
  }

  public async search(query: string): Promise<SellionsCalendarEvent[]> {
    if (!this.dataSources) return Promise.resolve([]);
    const promises = this.dataSources.map((source) => source.search(query));
    const events = await Promise.all(promises);
    return this.flattenEvents(events);
  }

  private flattenEvents(events: SellionsCalendarEvent[][]): SellionsCalendarEvent[] {
    return events.reduce((accumulator, value) => accumulator.concat(value), []);
  }

  public async getFiltersConfig(): Promise<FilterInput[]> {
    if (!this.dataSources) return Promise.resolve([]);
    const promises = this.dataSources.map((source) => source.getFiltersConfig());
    const filterConfigs = await Promise.all(promises);
    return this.flattenFilterConfig(filterConfigs);
  }

  private flattenFilterConfig(filterConfigs: FiltersConfig[]): FilterInput[] {
    return filterConfigs.reduce((accumulator, value) => accumulator.concat(value.filters), []);
  }

  public getFilters(): FilterOutput[] {
    return [].concat(...this.dataSources.map((source) => source.filters));
  }

  public mapEvents(events: SellionsCalendarEvent[]): CalendarEvent[] {
    return events.map((event) => ({
      start: new Date(event.dateFrom),
      end: moment(event.dateTo).isSame(moment(event.dateFrom).startOf('day')) ? moment(event.dateTo).add(-1, 'day').toDate() : new Date(event.dateTo),
      title: event.displayComponentData.title || '-',
      color: {
        primary: this.colors.primary,
        secondary: this.colors.secondary,
      },
    }));
  }

  public setColors(colors: { primary: string; secondary: string }) {
    this.colors = colors;
  }

  public nextDate(mode: 'forward' | 'backward', page: 'CalendarDayPage' | 'CalendarMonthPage'): void {
    if (this._loading.getValue() === true) {
      return;
    }

    let currentDate = this._selectedDate.getValue();
    currentDate.add(mode === 'forward' ? 1 : -1, page === 'CalendarDayPage' ? 'days' : 'months');
    this._selectedDate.next(currentDate);
  }

  public setDate(date: Date): void {
    this._selectedDate.next(moment(date));
  }

  public showEventDetail(event: SellionsEvent): void {
    const client: IClientModel = this.clientService.getById(event.clientId);
    this.app.getRootNav().push('EventsPage', { event, client, calendar: true });
  }
}



// WEBPACK FOOTER //
// ./src/modules/calendar/services/calendar-service/calendar-service.ts